import { useEffect } from "react";
import Grid from "@mui/material/Grid2";
import { useTranslate } from "@refinedev/core";

import { DatePicker, Select } from "@shared/components/DataEntry";
import { useQueryParams } from "@shared/hooks";
import {
  getPeriodRange,
  parseISODate,
  toISODate,
  UsagePeriod,
} from "@modules/usages/utils/period";

export function UsagePeriodFilter() {
  const translate = useTranslate();
  const { getQueryParams, setQueryParams } = useQueryParams();
  const params = getQueryParams();
  const period = (params.period as UsagePeriod) || "current";
  const isCustom = period === "custom";

  useEffect(() => {
    const current = getQueryParams();
    if (current.period || (current.created_at_after && current.created_at_before)) {
      return;
    }

    setQueryParams({
      ...current,
      period: "current",
      ...getPeriodRange("current"),
    });
  }, [params.period, params.created_at_after, params.created_at_before, getQueryParams, setQueryParams]);

  const updateParams = (updates: Record<string, string | undefined>) => {
    setQueryParams({
      ...params,
      ...updates,
      page: "1",
    });
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
      <Grid size={{ xs: 12, md: 4 }}>
        <Select
          label={translate("v1/usage_admin.filters.period")}
          value={period}
          options={[
            { value: "current", label: translate("v1/usage_admin.filters.period_current") },
            { value: "previous", label: translate("v1/usage_admin.filters.period_previous") },
            { value: "last_3", label: translate("v1/usage_admin.filters.period_last_3") },
            { value: "last_6", label: translate("v1/usage_admin.filters.period_last_6") },
            { value: "last_12", label: translate("v1/usage_admin.filters.period_last_12") },
            { value: "custom", label: translate("v1/usage_admin.filters.period_custom") },
          ]}
          onChange={(value) => {
            const nextPeriod = String(value) as UsagePeriod;
            if (nextPeriod === "custom") {
              updateParams({
                period: "custom",
                created_at_after: params.created_at_after,
                created_at_before: params.created_at_before,
              });
              return;
            }

            updateParams({
              period: nextPeriod,
              ...getPeriodRange(nextPeriod),
            });
          }}
        />
      </Grid>

      {isCustom && (
        <>
          <Grid size={{ xs: 12, md: 4 }}>
            <DatePicker
              label={translate("v1/usage_admin.filters.created_at_after")}
              value={params.created_at_after ? parseISODate(params.created_at_after) : null}
              onChange={(date) => {
                updateParams({
                  period: "custom",
                  created_at_after: date ? toISODate(date) : undefined,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DatePicker
              label={translate("v1/usage_admin.filters.created_at_before")}
              value={params.created_at_before ? parseISODate(params.created_at_before) : null}
              onChange={(date) => {
                updateParams({
                  period: "custom",
                  created_at_before: date ? toISODate(date) : undefined,
                });
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
